/**
 * Glucose (diabetic-CKD home monitoring) — all user-facing strings in one place.
 *
 * Same single-module pattern as SubQ; the text itself lives in the locale files
 * under `glucose.*` and is looked up when read. Reading-context vocabulary,
 * the target band and the red-flag copy were confirmed with the clinical owner (G7,
 * Stuart, 2026-07-22). Context keys are the values stored in the care-event payload;
 * CONTEXT_LABELS renders them back.
 *
 * The hypoglycemia emergency copy is standard feline first-aid (rub honey/corn syrup on
 * the gums, offer food, contact the vet) per felinediabetes.com hypo-emergency guidance;
 * it is deliberately action-first and non-diagnostic. It lives here so the owner can
 * reword it without touching component logic.
 */

import i18n from '../../i18n';

export const GLUCOSE_STRINGS = {
  // Page + navigation
  // Nav label and page heading are ONE string: they read "Blood glucose" and
  // "Blood Glucose" respectively, the only nav/heading pair in the app that
  // disagreed (finding 22).
  get pageTitle() { return i18n.t('glucose.pageTitle'); },
  get navLabel() { return i18n.t('glucose.navLabel'); },
  pageSubtitle: (catPossessive: string) => i18n.t('glucose.pageSubtitle', { catPossessive }),

  // Entry card
  get entryTitle() { return i18n.t('glucose.entryTitle'); },
  get entryIntro() { return i18n.t('glucose.entryIntro'); },

  get unitLabel() { return i18n.t('glucose.unitLabel'); },
  get valueLabel() { return i18n.t('glucose.valueLabel'); },
  get valueCustomLabelMgDl() { return i18n.t('glucose.valueCustomLabelMgDl'); },
  get valueCustomLabelMmol() { return i18n.t('glucose.valueCustomLabelMmol'); },

  get contextLabel() { return i18n.t('glucose.contextLabel'); },
  get contextOptionalNote() { return i18n.t('glucose.contextOptionalNote'); },

  get insulinLabel() { return i18n.t('glucose.insulinLabel'); },
  get insulinOptionalNote() { return i18n.t('glucose.insulinOptionalNote'); },
  get insulinUnit() { return i18n.t('glucose.insulinUnit'); },
  get insulinCustomLabel() { return i18n.t('glucose.insulinCustomLabel'); },

  get timeLabel() { return i18n.t('glucose.timeLabel'); },
  get timeDefaultNote() { return i18n.t('glucose.timeDefaultNote'); },
  get timeChange() { return i18n.t('glucose.timeChange'); },

  get save() { return i18n.t('glucose.save'); },
  get saving() { return i18n.t('glucose.saving'); },
  get savedTitle() { return i18n.t('glucose.savedTitle'); },
  get savedSubtext() { return i18n.t('glucose.savedSubtext'); },
  get savedOfflineTitle() { return i18n.t('glucose.savedOfflineTitle'); },
  get savedOfflineSubtext() { return i18n.t('glucose.savedOfflineSubtext'); },
  get pendingSyncBadge() { return i18n.t('glucose.pendingSyncBadge'); },
  get logAnother() { return i18n.t('glucose.logAnother'); },

  // Validation
  get errorNoValue() { return i18n.t('glucose.errorNoValue'); },
  get errorSaveFailed() { return i18n.t('glucose.errorSaveFailed'); },

  // Red-flag banners (entry-time). Levels mirror the engine classifier.
  get flagEmergencyTitle() { return i18n.t('glucose.flagEmergencyTitle'); },
  get flagEmergencyBody() { return i18n.t('glucose.flagEmergencyBody'); },
  get flagCautionTitle() { return i18n.t('glucose.flagCautionTitle'); },
  get flagCautionBody() { return i18n.t('glucose.flagCautionBody'); },
  get flagHighTitle() { return i18n.t('glucose.flagHighTitle'); },
  get flagHighBody() { return i18n.t('glucose.flagHighBody'); },

  // Curve view
  get curveTitle() { return i18n.t('glucose.curveTitle'); },
  get curveSubtitle() { return i18n.t('glucose.curveSubtitle'); },
  get curveDayPickerLabel() { return i18n.t('glucose.curveDayPickerLabel'); },
  get curveDaysHeading() { return i18n.t('glucose.curveDaysHeading'); },
  get curveDaysHint() { return i18n.t('glucose.curveDaysHint'); },
  get curveEmptyTitle() { return i18n.t('glucose.curveEmptyTitle'); },
  get curveEmptyDescription() { return i18n.t('glucose.curveEmptyDescription'); },
  get curveSingleReadingNote() { return i18n.t('glucose.curveSingleReadingNote'); },
  get curveBandLabel() { return i18n.t('glucose.curveBandLabel'); },

  // Multi-day trend
  get trendsTitle() { return i18n.t('glucose.trendsTitle'); },
  get trendsSubtitle() { return i18n.t('glucose.trendsSubtitle'); },
  get trendsLowLabel() { return i18n.t('glucose.trendsLowLabel'); },
  get trendsHighLabel() { return i18n.t('glucose.trendsHighLabel'); },
  get trendsEmptyTitle() { return i18n.t('glucose.trendsEmptyTitle'); },
  get trendsEmptyDescription() { return i18n.t('glucose.trendsEmptyDescription'); },
} as const;

/** Reading-context options (owner-confirmed set, 2026-07-22). Optional, single-choice.
 *  Keys are the values stored in detail.context. AMPS/PMPS = AM/PM pre-shot. */
export const CONTEXT_OPTIONS: { value: string; label: string }[] = [
  { value: 'pre_shot_am', get label() { return i18n.t('glucose.context.preShotAm'); } },
  { value: 'pre_shot_pm', get label() { return i18n.t('glucose.context.preShotPm'); } },
  { value: 'pre_meal', get label() { return i18n.t('glucose.context.preMeal'); } },
  { value: 'post_meal', get label() { return i18n.t('glucose.context.postMeal'); } },
  { value: 'spot', get label() { return i18n.t('glucose.context.spot'); } },
];

/** Labels looked up when read (the same text as CONTEXT_OPTIONS). */
export const CONTEXT_LABELS: Record<string, string> = Object.defineProperties(
  {} as Record<string, string>,
  Object.fromEntries(
    CONTEXT_OPTIONS.map((o) => [
      o.value,
      { get: () => o.label, enumerable: true, configurable: true },
    ]),
  ),
);
